import React from 'react';
import { X, Star, User } from 'lucide-react';

interface Player {
  id: string;
  nome: string;
  posicao: string;
  nivel: number;
}

interface PositionSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  position: {
    id: string;
    name: string;
    position: string;
  } | null;
  availablePlayers: Player[];
  onSelectPlayer: (player: Player) => void;
}

const PositionSelector: React.FC<PositionSelectorProps> = ({
  isOpen,
  onClose,
  position,
  availablePlayers,
  onSelectPlayer
}) => {
  if (!isOpen || !position) return null;

  // Filtrar jogadores compatíveis com a posição
  const compatiblePlayers = availablePlayers.filter(player => {
    // Filtro restrito: apenas jogadores com a posição exata
    return player.posicao === position.position;
  });

  const getPositionColor = (posicao: string) => {
    switch (posicao) {
      case 'goleiro':
        return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400';
      case 'zagueiro':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400';
      case 'lateral':
        return 'bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400';
      case 'meio-campo':
        return 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400';
      case 'atacante':
        return 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-dark-paper rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b dark:border-dark-border">
          <h2 className="text-xl font-bold">Selecionar Jogador</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-light rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          <div className="mb-4">
            <h3 className="font-medium text-gray-700 dark:text-gray-300">
              Posição: <span className="text-primary-600 dark:text-primary-400 capitalize">{position.name}</span>
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Selecione um jogador para esta posição
            </p>
          </div>

          {compatiblePlayers.length === 0 ? (
            <div className="text-center py-8">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                Nenhum jogador disponível para esta posição
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {compatiblePlayers.map((player) => (
                <button
                  key={player.id}
                  onClick={() => {
                    onSelectPlayer(player);
                    onClose();
                  }}
                  className="w-full p-4 text-left border dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-light transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center text-sm font-medium">
                        {player.nome.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{player.nome}</p>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getPositionColor(player.posicao)}`}>
                            {player.posicao}
                          </span>
                          <div className="flex">
                            {Array.from({ length: player.nivel }).map((_, i) => (
                              <Star key={i} className="w-3 h-3 text-yellow-400 fill-current" />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PositionSelector;